/*
 * GameInfoModal.tsx
 * Full-screen modal overlay that displays game instructions and how to play information.
 * Features a centered layout with icon, instructions, and call-to-action button.
 */
'use client';

import { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@components';
import { getHighlightMarkup } from '@/core/utils';

// Config
import { DEBUG_LOGS } from '../config/constants';

// Styles
import classNames from 'classnames/bind';
import styles from './GameInfoModal.module.scss';
const cx = classNames.bind(styles);

// Debug
import { useDebug } from "@/utils/useDebugLog";

// Assets
import InfoIllustration01 from './assets/info-illustration-01.svg?url';
import InfoIllustration02 from './assets/info-illustration-02.svg?url';
import InfoIllustration03 from './assets/info-illustration-03.svg?url';

// --------------------

interface GameInfoModalProps {
  isVisible: boolean;
  onClose: () => void;
  copy?: {
    eyebrow: string;
    headline: string;
    ctaButton: string;
    ariaLabel?: string;
    stepOne?: {
      headline?: string;
      subhead?: string;
    };
    stepTwo?: {
      headline?: string;
      subhead?: string;
    };
    stepThree?: {
      headline?: string;
      subhead?: string;
    };
  };
}

const GameInfoModal: React.FC<GameInfoModalProps> = ({ isVisible, onClose, copy }) => {
  const { log } = useDebug({ name: 'GameInfoModal', bgColor: '#FF4858', enabled: DEBUG_LOGS });

  const isFirstRender = useRef(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const headlineMarkup = getHighlightMarkup(copy?.headline);

  // Handle close action
  const handleClose = () => {
    log('Info modal closed');
    onClose();
  };

  // Track if this is first render to skip animation
  useEffect(() => {
    if (isVisible && isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, [isVisible]);

  // Close on Escape key
  useEffect(() => {
    if (!isVisible) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isVisible]);

  // Focus trap - lock focus within modal when open
  useEffect(() => {
    if (!isVisible || typeof document === 'undefined') return;

    // Focus first interactive element when modal opens
    const focusFirstElement = () => {
      const focusableElements = contentRef.current?.querySelectorAll(
        'button:not([disabled]):not([tabindex="-1"]), a[href]:not([tabindex="-1"])'
      );

      if (focusableElements && focusableElements.length > 0) {
        const firstElement = focusableElements[0] as HTMLElement;
        firstElement?.focus();
      }
    };

    // Small delay to ensure modal animation has started
    const timer = setTimeout(focusFirstElement, 100);

    // Trap Tab key within modal
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const focusableElements = contentRef.current?.querySelectorAll(
        'button:not([disabled]):not([tabindex="-1"]), a[href]:not([tabindex="-1"])'
      );

      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      // Check if currently focused element is inside the modal
      const isInsideModal = contentRef.current?.contains(document.activeElement as Node);

      if (!isInsideModal) {
        // Focus escaped modal - bring it back to first element
        e.preventDefault();
        firstElement?.focus();
        return;
      }

      // Shift + Tab on first element - go to last
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      }
      // Tab on last element - go to first
      else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isVisible]);

  // ---------------------- Render

  return (
    <AnimatePresence>
      {isVisible && (
        <div className={cx('game-info-modal')}>

          {/* Dark overlay */}
          <motion.div
            className={cx('overlay')}
            initial={isFirstRender.current ? { opacity: 1 } : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
            onClick={handleClose}
          />

          {/* Modal Content */}
          <motion.div
            ref={contentRef}
            className={cx('content')}
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.92 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1], delay: 0.1 }}
          >

            {/* Top section */}
            <div className={cx('game-info-modal-top')}>

              {/* Eyebrow */}
              <div className={cx('eyebrow-l', 'eyebrow')}>{copy?.eyebrow}</div>

              {/* Headline */}
              <h1 className={cx('title-lc-m', 'headline')} dangerouslySetInnerHTML={{ __html: headlineMarkup }}></h1>
            </div>

            <div className={cx('divider')} />

            {/* ------------------------------ Columns */}

            <div className={cx('instruction-row')}>

              {/* Item 01 */}
              <div className={cx('instruction-column')}>
                <div className={cx('instruction-image')}>
                  <img src={InfoIllustration01.src} alt="" />
                </div>
                <h3 className={cx('title-lc-xs', 'instruction-heading')}>{copy?.stepOne?.headline}</h3>
                <p className={cx('eyebrow-s', 'instruction-subheading')}>{copy?.stepOne?.subhead}</p>
              </div>

              {/* Item 02 */}
              <div className={cx('instruction-column')}>
                <div className={cx('instruction-image')}>
                  <img src={InfoIllustration02.src} alt="" />
                </div>
                <h3 className={cx('title-lc-xs', 'instruction-heading')}>{copy?.stepTwo?.headline}</h3>
                <p className={cx('eyebrow-s', 'instruction-subheading')}>{copy?.stepTwo?.subhead}</p>
              </div>

              {/* Item 03 */}
              <div className={cx('instruction-column')}>
                <div className={cx('instruction-image')}>
                  <img src={InfoIllustration03.src} alt="" />
                </div>
                <h3 className={cx('title-lc-xs', 'instruction-heading')}>{copy?.stepThree?.headline}</h3>
                <p className={cx('eyebrow-s', 'instruction-subheading')}>{copy?.stepThree?.subhead}</p>
              </div>

            </div>

            {/* ------------------------------ */}

            {/* Button */}
            <Button
              label={copy?.ctaButton}
              onClick={handleClose}
              ariaLabel={copy?.ariaLabel || copy?.ctaButton}
            />

          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default GameInfoModal;
